import chain, { concat, intersectionWith, isEmpty } from "lodash"; // See: https://github.com/vercel/next.js/issues/52188
import Link from "next/link";
import { useRouter } from "next/router";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Messages as PrimeMessages } from "primereact/messages";
import { classNames } from "primereact/utils";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useTrackEvent } from "../../../../common/api/useTrackEvent";
import { DataTable, Divider, Typography } from "../../../../common/components";
import { Messages } from "../../../../common/components/Messages";
import { Modal } from "../../../../common/components/Modal";
import { UploadFileModal } from "../../../../common/components/UploadFileModal";
import { DEFAULT_LOCALE, SPACES_TEMPLATE } from "../../../../common/constants";
import { ANALYTICS_EVENTS } from "../../../../common/constants/analytics.constants";
import { TABLE_IDS } from "../../../../common/store/useTableStore";
import { useSpaces } from "../../../spaces/api/useSpaces";
import { Space } from "../../../spaces/models";
import { FLOW_TYPES, START_LINE_CSV, STEPS } from "../../constants";
import { useSetUpSpacesStore } from "../../store";
import { DialogStepper } from "../DialogStepper";

const AddSpacesFlowModal = () => {
    const {
        toggleVisibility,
        setStep,
        addSpace,
        setSpaces,
        removeSpace,
        spaces,
        flowType,
    } = useSetUpSpacesStore();

    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const { t } = useTranslation(["common", "setUpSpaces", "spaces"]);

    const { trackEvent } = useTrackEvent();

    const [manualSpaceValue, setManualSpaceValue] = useState("");
    const [existingSpace, setExistingSpace] = useState("");
    const [isImporting, setIsImporting] = useState(false);
    const [csvFile, setCsvFile] = useState<File>();
    const [fileSpacesCount, setFileSpacesCount] = useState(0);

    const messagesRef = useRef<PrimeMessages>(null);

    const { spaces: allSpacesFromLocation } = useSpaces({
        // Only fetch for Add Spaces flow on an existing location
        companyId: flowType === FLOW_TYPES.ADD_SPACES ? companyId : undefined,
        locationId,
    });

    const isDuplicateSpace = (spaceName: string) =>
        allSpacesFromLocation.some(
            (space: Space) =>
                space.name.toLowerCase() === spaceName.toLowerCase()
        );

    const duplicateSpaces = useMemo(
        () =>
            intersectionWith(
                spaces,
                allSpacesFromLocation,
                (newSpace, space: Space) =>
                    newSpace.name.toLowerCase() === space.name.toLowerCase()
            ),
        [allSpacesFromLocation, spaces]
    );
    const isEmptyDuplicateSpaces = isEmpty(duplicateSpaces);
    const canGoNext = spaces?.length > 0;
    const canGoBack = flowType !== FLOW_TYPES.ADD_SPACES || !!csvFile;

    useEffect(() => {
        // The timeout is for the messages ref to have time to be initialized by the library
        setTimeout(() => {
            messagesRef.current?.clear();
            if (isEmptyDuplicateSpaces) {
                return;
            }
            messagesRef.current?.show({
                id: "duplicates-error",
                severity: "error",
                summary: t("common:error"),
                detail: <Trans i18nKey="setUpSpaces:spaces.uploadDuplicates" />,
                sticky: true,
                closable: false,
            });
        }, 100);
    }, [isEmptyDuplicateSpaces, t]);

    const handleClickNext = () => {
        if (!canGoNext) {
            return;
        }

        // Remove duplicate spaces
        setSpaces(spaces.filter((space) => !isDuplicateSpace(space.name)));
        setStep(STEPS.ADD_SPACE_PURPOSES);
    };

    const handleClickBack = () => {
        if (csvFile) {
            setIsImporting(true);
            setCsvFile(undefined);
            setSpaces([]);
            setFileSpacesCount(0);
            return;
        }
        setStep(STEPS.SET_ADDRESS);
    };

    const handleManualSpaceValueChange = (
        value: ChangeEvent<HTMLInputElement>
    ) => {
        setManualSpaceValue(value.target.value);
    };

    const handleAddSpace = () => {
        if (!manualSpaceValue.trim()) {
            return;
        }

        setManualSpaceValue("");

        const spaceExists = concat(spaces, allSpacesFromLocation).some(
            (space) =>
                space.name.toLowerCase() === manualSpaceValue.toLowerCase()
        );
        if (spaceExists) {
            setExistingSpace(manualSpaceValue);
            return;
        }

        trackEvent(ANALYTICS_EVENTS.ADD_SPACE_MANUALLY, {
            "Count of Rooms": spaces.length - fileSpacesCount + 1,
        });
        addSpace(manualSpaceValue);
    };

    const handleReadFile = (lines: string[][], file?: File) => {
        const uniqueSpaces = chain(lines)
            .slice(START_LINE_CSV)
            .map((line) => {
                const [name, externalId] = line.map((item) => item.trim());
                return name ? { name, externalId } : null;
            })
            .compact()
            .uniqBy("name")
            .value();
        setSpaces(uniqueSpaces);
        setIsImporting(false);
        setCsvFile(file);
        setFileSpacesCount(uniqueSpaces.length);
        trackEvent(ANALYTICS_EVENTS.ADD_SPACE_VIA_CSV_UPLOAD, {
            "Count of Rooms": uniqueSpaces.length,
        });
    };

    const handleInputFocus = () => {
        setExistingSpace("");
    };

    const getTitle = () => {
        if (flowType === FLOW_TYPES.ADD_SPACES) {
            return t("spaces:addNewSpaces");
        }
        return t("setUpSpaces:spaces.title");
    };

    const SpacesList = () => {
        if (isEmpty(spaces)) {
            return null;
        }

        const rowClassName = (data: Space) => ({
            "!bg-error-light-80": isDuplicateSpace(data.name),
        });

        const ActionBody = (data: any, options: ColumnBodyOptions) => {
            const { rowIndex } = options;

            const handleRemoveSpace = () => {
                removeSpace(rowIndex);
                setFileSpacesCount((prevValue) => Math.max(0, prevValue - 1));
            };

            return (
                <Button
                    icon={PrimeIcons.TIMES}
                    rounded
                    text
                    severity="secondary"
                    className="!p-0 !h-6 !w-6 text-default"
                    onClick={handleRemoveSpace}
                />
            );
        };

        return (
            <DataTable
                id={TABLE_IDS.ADD_SPACES}
                size="small"
                value={spaces}
                scrollable
                scrollHeight="15.3rem"
                pt={{
                    headerRow: {
                        className: "h-14 [&>th]:!pl-6",
                    },
                }}
                rowClassName={rowClassName}
                className="w-fit min-w-[30.2rem]"
            >
                <Column
                    field="name"
                    header={t("common:space")}
                    className="font-semibold !pl-6"
                />
                <Column body={ActionBody} className="w-14" />
            </DataTable>
        );
    };

    return (
        <>
            <Modal
                isOpen={!isImporting}
                title={getTitle()}
                hasCancelButton={canGoBack}
                hasOkButton
                cancelText={t("common:back") || ""}
                okText={t("common:next") || ""}
                isOkButtonDisabled={!canGoNext}
                onClickClose={toggleVisibility}
                onClickOk={handleClickNext}
                onClickCancel={handleClickBack}
                className="max-w-full"
            >
                <div className="flex flex-col gap-4">
                    <DialogStepper activeStep={STEPS.ADD_SPACES} />
                    <h3 className="text-lg">{t("setUpSpaces:spaces.intro")}</h3>
                    <Messages ref={messagesRef} />

                    {/* Import */}
                    <h3 className="text-lg">
                        {t("setUpSpaces:spaces.importSubtitle")}
                    </h3>
                    {!csvFile && (
                        <>
                            <Typography>
                                <Trans
                                    i18nKey="setUpSpaces:spaces.importDescription"
                                    components={{
                                        link1: (
                                            <Link
                                                className="text-info-default underline"
                                                href={
                                                    router.locale ===
                                                    DEFAULT_LOCALE
                                                        ? SPACES_TEMPLATE.de
                                                        : SPACES_TEMPLATE.en
                                                }
                                            />
                                        ),
                                    }}
                                />
                            </Typography>
                            <Button
                                severity="secondary"
                                disabled={canGoNext}
                                onClick={() =>
                                    !canGoNext && setIsImporting(true)
                                }
                                icon="pi pi-file"
                                iconPos="right"
                                className="w-fit"
                                label={t("common:upload") || ""}
                            />
                            <Divider align="center" className="w-[70%] !m-auto">
                                <Typography className="font-light">
                                    {t("common:or")}
                                </Typography>
                            </Divider>
                        </>
                    )}
                    {!!csvFile && (
                        <>
                            <Typography>{csvFile?.name}</Typography>
                            <SpacesList />
                        </>
                    )}

                    {/* Manual */}
                    <h3 className="text-lg">
                        {t("setUpSpaces:spaces.manuallySubtitle")}
                    </h3>
                    <div className="max-w-sm">
                        <div className="p-inputgroup flex-1">
                            <InputText
                                placeholder={
                                    existingSpace ||
                                    t(
                                        "setUpSpaces:spaces.inputPlaceholder",
                                        ""
                                    ) ||
                                    ""
                                }
                                value={manualSpaceValue}
                                onChange={handleManualSpaceValueChange}
                                onFocus={handleInputFocus}
                                className={classNames({
                                    "p-invalid": existingSpace,
                                })}
                            />
                            <Button
                                label={t("common:add") || ""}
                                icon="pi pi-plus"
                                severity="secondary"
                                iconPos="right"
                                onClick={handleAddSpace}
                            />
                        </div>
                        <small className="text-error-default">
                            {existingSpace &&
                                t("setUpSpaces:spaces.spaceExists")}
                        </small>
                    </div>

                    {!csvFile && <SpacesList />}
                </div>
            </Modal>
            <UploadFileModal
                isOpen={isImporting}
                title={t("setUpSpaces:spaces.uploadTitle") || ""}
                onCancel={() => setIsImporting(false)}
                onClose={toggleVisibility}
                onReadLines={handleReadFile}
            />
        </>
    );
};

export default AddSpacesFlowModal;
